import { useState } from "react";
import { ArrowRight, Check, ChevronDown, Globe2, Sparkles } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { localeTagFor } from "@/i18n/locale";
import { PREMIUM_BENEFIT_KEYS, PREMIUM_CHAT_PER_MONTH, PREMIUM_VOICE_MINUTES, PREMIUM_LIVE_MINUTES } from "@/config/pricing";
import { APP_STORE_URL, defaultLandingMarket, formatLandingPrice, readLandingMarket, saveLandingMarket, type LandingMarket } from "@/config/landingPricing";

interface LandingPlansProps {
  onStartFree: () => void;
  onPremium: () => void;
  onStore: () => void;
}

const FREE_BENEFITS = [1, 2, 3, 4, 5, 6].map(n => `pricing.free.f${n}`);
const QUOTAS = [
  { id: "chat", value: PREMIUM_CHAT_PER_MONTH },
  { id: "voice", value: PREMIUM_VOICE_MINUTES },
  { id: "live", value: PREMIUM_LIVE_MINUTES },
] as const;

export function LandingPlans({ onStartFree, onPremium, onStore }: LandingPlansProps) {
  const { lang, t } = useLanguage();
  const [chosenMarket, setChosenMarket] = useState<LandingMarket | null>(readLandingMarket);
  const market = chosenMarket ?? defaultLandingMarket(lang);

  const chooseMarket = (next: LandingMarket) => {
    setChosenMarket(next);
    saveLandingMarket(next);
  };

  return (
    <>
      <div className="landing-section-heading">
        <p className="landing-eyebrow">{t("landing.pricing.kicker")}</p>
        <h2 id="plans-title">{t("landing.pricing.title")}</h2>
        <p>{t("landing.market.promise")}</p>
      </div>
      <div className="landing-market">
        <p id="landing-market-label"><Globe2 size={16} aria-hidden="true" />{t("landing.market.label")}</p>
        <div className="landing-market-options" role="group" aria-labelledby="landing-market-label">
          {(["BR", "EU"] as const).map(option => (
            <button key={option} type="button" aria-pressed={market === option} onClick={() => chooseMarket(option)}>
              {t(`landing.market.${option}`)}
              {market === option && <Check size={15} aria-hidden="true" />}
            </button>
          ))}
        </div>
      </div>
      <a className="landing-market-summary" href="#landing-premium">
        <span><strong>Premium</strong><small>{t(`landing.market.channel.${market}`)}</small></span>
        <span>{formatLandingPrice(market, lang)}<small>{t("landing.pricing.perMonth")}</small></span>
        <ChevronDown size={18} aria-hidden="true" />
      </a>
      <div className="landing-plan-grid">
        <article className="landing-plan">
          <p className="landing-plan-badge">{t("landing.market.freeBadge")}</p>
          <div className="landing-plan-heading"><h3>Free</h3></div>
          <p className="landing-price" aria-live="polite" aria-atomic="true">{formatLandingPrice(market, lang, true)}</p>
          <p className="landing-plan-intro">{t("landing.v4.plan.free")}</p>
          <ul className="landing-plan-features">
            {FREE_BENEFITS.map(key => <li key={key}><Check size={16} aria-hidden="true" /><span>{t(key)}</span></li>)}
          </ul>
          <button className="landing-outline landing-plan-cta" onClick={onStartFree}>{t("landing.pricing.free.cta")}<ArrowRight size={17} aria-hidden="true" /></button>
        </article>
        <article id="landing-premium" className="landing-plan landing-plan-premium">
          <p className="landing-plan-badge"><Sparkles size={14} aria-hidden="true" />{t("landing.market.premiumBadge")}</p>
          <div className="landing-plan-heading"><h3>Premium</h3><Sparkles size={22} aria-hidden="true" /></div>
          <p className="landing-price" aria-live="polite" aria-atomic="true">{formatLandingPrice(market, lang)}<span>{t("landing.pricing.perMonth")}</span></p>
          <p className="landing-plan-channel">{t(`landing.market.channel.${market}`)}</p>
          <p className="landing-plan-intro">{t("landing.v4.plan.premium")}</p>
          <dl className="landing-plan-quotas">
            {QUOTAS.map(({ id, value }) => <div key={id}><dt>{t(`landing.market.quota.${id}`)}</dt><dd>{value.toLocaleString(localeTagFor(lang))}</dd></div>)}
          </dl>
          <ul className="landing-plan-features">
            {PREMIUM_BENEFIT_KEYS.slice(0, 5).map(key => <li key={key}><Check size={16} aria-hidden="true" /><span>{t(key)}</span></li>)}
          </ul>
          <details className="landing-plan-more">
            <summary>{t("landing.v4.plan.more")}<ChevronDown size={16} aria-hidden="true" /></summary>
            <ul className="landing-plan-features">{PREMIUM_BENEFIT_KEYS.slice(5).map(key => <li key={key}><Check size={16} aria-hidden="true" /><span>{t(key)}</span></li>)}</ul>
          </details>
          {market === "EU"
            ? <a className="landing-store landing-plan-cta" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={onStore}>{t("landing.market.storeCta")}<ArrowRight size={17} aria-hidden="true" /></a>
            : <button className="landing-store landing-plan-cta" onClick={onPremium}>{t("pricing.core.cta")}<ArrowRight size={17} aria-hidden="true" /></button>}
          <p className="landing-plan-renewal">{t("landing.market.renewal")}</p>
        </article>
      </div>
      <p className="landing-pricing-note">{t(`landing.market.note.${market}`)}</p>
      <details className="landing-market-help">
        <summary>{t("landing.market.faq.q")}<ChevronDown size={16} aria-hidden="true" /></summary>
        <p>{t("landing.market.faq.a")}</p>
      </details>
    </>
  );
}
